<template>
	<div>
		<!-- `inline` (opt-in): isi form dirender langsung di halaman (mis. tab User Request New_CataloguerForm), bukan modal.
			 Elemen biasa cuma merender slot default -> header/footer modal otomatis tidak ikut. Tanpa `inline` = DialogPrime seperti dulu. -->
		<component :is="inline ? 'div' : 'DialogPrime'" v-bind="wrapperAttrs" @hide="onHide">
			<template #header>
				<div class="d-flex align-items-center gap-2">
					<div>
						<h5 class="mb-0 fw-bold text-dark">{{ readonly ? 'View Item' : (dialog.editingItemId ? 'Edit Item' : 'Add New Item') }}</h5>
					</div>
				</div>
			</template>

			<template #footer>
				<div class="d-flex justify-content-end gap-2 pt-3">
					<ButtonPrime label="Cancel" severity="secondary" outlined :disabled="dialog.saving" @click="onHide" />
					<ButtonPrime v-if="!readonly" label="Save Item" :loading="dialog.saving" @click="saveItem" />
				</div>
			</template>

			<div class="container-fluid px-0">
				<div
					v-if="isTabVisibleFn(BASIC_DATA_TAB)"
					class="card mb-2 mt-3"
					:class="{ 'border-danger': dialog.errors.basicData }"
				>
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('basicData')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">Basic Data & Specification</span>
								<span v-if="isSectionMandatoryFn('Basic Data')" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.basicData" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.basicData }}
							</small>
						</div>

						<i :class="dialog.sections.basicData ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.basicData" class="card-body pt-0">
						<div class="row g-3">
							<div v-if="isFieldVisibleFn('material_number', BASIC_DATA_TAB)" class="col-12 mb-2">
								<label for="material-number" class="form-label fw-semibold">
									Material Number <span v-if="isFieldMandatoryFn('material_number', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<AutoComplete id="material-number" v-model="dialog.form.material_number" size="small"
									:suggestions="dialog.options.material_number" option-label="label" :force-selection="false"
									placeholder="Search Material Number..." :min-length="2" :delay="1000" :loading="dialog.loading.material_number"
									:disabled="!isFieldEnabledFn('material_number', BASIC_DATA_TAB)" :invalid="isFieldInvalidFn('material_number')" fluid
									@complete="searchMaterialNumber" @item-select="onMaterialNumberSelect">
									<template #option="{ option }">
										<div>
											<div style="font-size:0.85rem;font-weight:600">{{ option.label }}</div>
											<div style="font-size:0.75rem;color:#6b7280">{{ option.short_text }}</div>
										</div>
									</template>

									<template #empty>
										<div v-if="dialog.loading.material_number" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</AutoComplete>
							</div>

							<div v-if="isFieldVisibleFn('material_type', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="material-type" class="form-label fw-semibold">
									Material Type <span v-if="isFieldMandatoryFn('material_type', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.material_type"
									size="small"
									:options="dialog.options.material_type"
									option-label="label"
									option-value="value"
									placeholder="Select Material Type" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('material_type', BASIC_DATA_TAB) || dialog.loading.material_type"
									:invalid="isFieldInvalidFn('material_type')" class="w-100"
									@show="onFieldOptionShow('material_type', BASIC_DATA_TAB)"
									@change="onFieldDependencyChange('material_type', BASIC_DATA_TAB); onAutomationSourceChange()"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.material_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.material_type, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.material_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('material_group', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="material-group" class="form-label fw-semibold">
									Material Group <span v-if="isFieldMandatoryFn('material_group', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.material_group"
									size="small"
									:options="dialog.options.material_group"
									option-label="label"
									option-value="value"
									placeholder="Select Material Group" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('material_group', BASIC_DATA_TAB) || !isFieldDependencyReadyFn('material_group', BASIC_DATA_TAB) || dialog.loading.material_group"
									:invalid="isFieldInvalidFn('material_group')" class="w-100"
									@show="onFieldOptionShow('material_group', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.material_group" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.material_group, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.material_group" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('service_category', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="service-category" class="form-label fw-semibold">
									Service Category <span v-if="isFieldMandatoryFn('service_category', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.service_category"
									size="small"
									:options="dialog.options.service_category"
									option-label="label"
									option-value="value"
									placeholder="Select Service Category" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('service_category', BASIC_DATA_TAB) || dialog.loading.service_category"
									:invalid="isFieldInvalidFn('service_category')" class="w-100"
									@show="onFieldOptionShow('service_category', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.service_category" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.service_category, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.service_category" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('service_group', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="service-group" class="form-label fw-semibold">
									Service Group <span v-if="isFieldMandatoryFn('service_group', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.service_group"
									size="small"
									:options="dialog.options.service_group"
									option-label="label"
									option-value="value"
									placeholder="Select Service Group" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('service_group', BASIC_DATA_TAB) || dialog.loading.service_group"
									:invalid="isFieldInvalidFn('service_group')" class="w-100"
									@show="onFieldOptionShow('service_group', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.service_group" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.service_group, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.service_group" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('uom', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="uom" class="form-label fw-semibold">
									UoM <span v-if="isFieldMandatoryFn('uom', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.uom"
									size="small"
									:options="dialog.options.uom"
									option-label="label"
									option-value="value"
									placeholder="Select UoM" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('uom', BASIC_DATA_TAB) || dialog.loading.uom"
									:invalid="isFieldInvalidFn('uom')" class="w-100"
									@show="onFieldOptionShow('uom', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.uom" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.uom, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.uom" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('plant', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="plant" class="form-label fw-semibold">
									Plant <span v-if="isFieldMandatoryFn('plant', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<!-- Plant multi-select HANYA untuk material type ZFGD (lihat `multiple` di form config
								FORM01); selain itu plant tetap satu pilihan seperti sebelumnya. Tab MRP tetap satu set
								nilai -- BE menyalinnya ke satu baris emcr_item_plants per plant yang dipilih (lihat
								UserFormService::saveEmcrItemPlants). Mesin config cuma kenal satu nilai, jadi plant
								PERTAMA yang dipakai buat filter lookup / query param data_source (lihat
								fieldResolvers.plant & primaryPlant()). -->
								<MultiSelect v-if="plantIsMultiple" v-model="dialog.form.plant"
									size="small"
									:options="dialog.options.plant"
									option-label="label"
									option-value="value"
									placeholder="Select Plant" filter display="chip" show-clear
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('plant', BASIC_DATA_TAB) || dialog.loading.plant"
									:invalid="isFieldInvalidFn('plant')" class="w-100 plant-multiselect"
									@show="onFieldOptionShow('plant', BASIC_DATA_TAB)"
									@change="onAutomationSourceChange()"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #empty>
										<div v-if="dialog.loading.plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</MultiSelect>

								<SelectPrime v-else v-model="plantSingleModel"
									size="small"
									:options="dialog.options.plant"
									option-label="label"
									option-value="value"
									placeholder="Select Plant" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('plant', BASIC_DATA_TAB) || dialog.loading.plant"
									:invalid="isFieldInvalidFn('plant')" class="w-100"
									@show="onFieldOptionShow('plant', BASIC_DATA_TAB)"
									@change="onAutomationSourceChange()"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.plant, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('valuation_class', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="valuation-class" class="form-label fw-semibold">
									Valuation Class <span v-if="isFieldMandatoryFn('valuation_class', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.valuation_class"
									size="small"
									:options="dialog.options.valuation_class"
									option-label="label"
									option-value="value"
									placeholder="Select Valuation Class" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('valuation_class', BASIC_DATA_TAB) || dialog.loading.valuation_class"
									:invalid="isFieldInvalidFn('valuation_class')" class="w-100"
									@show="onFieldOptionShow('valuation_class', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.valuation_class" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.valuation_class, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.valuation_class" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('equipment', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="equipment" class="form-label fw-semibold">
									Equipment <span v-if="isFieldMandatoryFn('equipment', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.equipment"
									size="small"
									:options="dialog.options.equipment"
									option-label="label"
									option-value="value"
									placeholder="Select Equipment" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('equipment', BASIC_DATA_TAB) || dialog.loading.equipment"
									:invalid="isFieldInvalidFn('equipment')" class="w-100"
									@show="onFieldOptionShow('equipment', BASIC_DATA_TAB, { search: '' })"
									@filter="searchSelectFieldOptions($event, 'equipment', { tab: BASIC_DATA_TAB })"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.equipment" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.equipment, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.equipment" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('currency', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="currency" class="form-label fw-semibold">
									Currency <span v-if="isFieldMandatoryFn('currency', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.currency"
									size="small"
									:options="dialog.options.currency"
									option-label="label"
									option-value="value"
									placeholder="Select Currency" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('currency', BASIC_DATA_TAB) || dialog.loading.currency"
									:invalid="isFieldInvalidFn('currency')" class="w-100"
									@show="onFieldOptionShow('currency', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.currency" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.currency, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.currency" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('price_unit', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="price-unit" class="form-label fw-semibold">
									Price Unit <span v-if="isFieldMandatoryFn('price_unit', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="price-unit" v-model="dialog.form.price_unit" size="small" placeholder="Enter Price Unit"
									:disabled="!isFieldEnabledFn('price_unit', BASIC_DATA_TAB)" :invalid="isFieldInvalidFn('price_unit')" fluid />
							</div>

							<div v-if="isFieldVisibleFn('estimated_annual_usage', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="estimated-annual-usage" class="form-label fw-semibold">
									Estimated Annual Usage <span v-if="isFieldMandatoryFn('estimated_annual_usage', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="estimated-annual-usage" v-model="dialog.form.estimated_annual_usage" size="small" placeholder="Enter Estimated Annual Usage"
									:disabled="!isFieldEnabledFn('estimated_annual_usage', BASIC_DATA_TAB)" :invalid="isFieldInvalidFn('estimated_annual_usage')" fluid />
							</div>

							<div v-if="isFieldVisibleFn('standard_description', BASIC_DATA_TAB)" class="col-md-6 mb-2">
								<label for="standard-description" class="form-label fw-semibold">
									Standard Description <span v-if="isFieldMandatoryFn('standard_description', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.standard_description"
									size="small"
									:options="dialog.options.standard_description"
									option-label="label"
									option-value="value"
									placeholder="Select Standard Description" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('standard_description', BASIC_DATA_TAB) || dialog.loading.standard_description"
									:invalid="isFieldInvalidFn('standard_description')" class="w-100"
									@show="onFieldOptionShow('standard_description', BASIC_DATA_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.standard_description" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.standard_description, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.standard_description" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('repairable_item', BASIC_DATA_TAB)" class="col-12 mb-2">
								<label for="repairable-item" class="form-label fw-semibold d-block">
									Repairable Item <span v-if="isFieldMandatoryFn('repairable_item', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<div class="d-flex align-items-center gap-2">
									<ToggleSwitch v-model="dialog.form.repairable_item" input-id="repairable-item"
										true-value="1" false-value="0"
										:disabled="!isFieldEnabledFn('repairable_item', BASIC_DATA_TAB)"
										:invalid="isFieldInvalidFn('repairable_item')" />
									<span class="text-muted">{{ dialog.form.repairable_item === '1' ? 'Yes' : 'No' }}</span>
								</div>
							</div>

							<div v-if="isFieldVisibleFn('specification', BASIC_DATA_TAB)" class="col-12 mb-2">
								<label for="specification" class="form-label fw-semibold">
									Specification <span v-if="isFieldMandatoryFn('specification', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<PrimeTextarea id="specification" v-model="dialog.form.specification" rows="10" placeholder="Enter Specification"
									:disabled="!isFieldEnabledFn('specification', BASIC_DATA_TAB)" :invalid="isFieldInvalidFn('specification')" size="small" fluid />
							</div>

							<div v-if="isFieldVisibleFn('notes', BASIC_DATA_TAB)" class="col-12 mb-2">
								<label for="notes" class="form-label fw-semibold">
									Notes <span v-if="isFieldMandatoryFn('notes', BASIC_DATA_TAB)" class="text-danger">*</span>
								</label>
								<PrimeTextarea id="notes" v-model="dialog.form.notes" rows="4" placeholder="Enter Notes"
									:disabled="!isFieldEnabledFn('notes', BASIC_DATA_TAB)" :invalid="isFieldInvalidFn('notes')" fluid />
							</div>
						</div>
					</div>
				</div>

				<div v-if="isTabVisibleFn(MANUFACTURE_TAB)" class="card mb-2 mt-3" :class="{ 'border-danger': dialog.errors.manufacturerPartNumber }">
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('manufacturerPartNumber')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">Manufacturer Part Number</span>
								<span v-if="isSectionMandatoryFn('Manufacture')" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.manufacturerPartNumber" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.manufacturerPartNumber }}
							</small>
						</div>

						<i :class="dialog.sections.manufacturerPartNumber ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.manufacturerPartNumber" class="card-body pt-0">
						<DataTable :value="dialog.form.emcr_manufacturers" class="p-datatable-sm p-datatable-gridlines" scrollable>
							<Column v-if="isColumnVisibleFn('old_manufacture', 'emcr_manufacturers', MANUFACTURE_TAB)" header="Old Manufacture" header-class="bg-light">
								<template #body="{ data }">
									<span class="text-muted">{{ data.old_manufacture || '-' }}</span>
								</template>
							</Column>

							<Column v-if="isColumnVisibleFn('old_manufacture_ref', 'emcr_manufacturers', MANUFACTURE_TAB)" header="Old Type/Model/Part Number" header-class="bg-light">
								<template #body="{ data }">
									<span class="text-muted">{{ data.old_manufacture_ref || '-' }}</span>
								</template>
							</Column>

							<Column v-if="isColumnVisibleFn('manufacture', 'emcr_manufacturers', MANUFACTURE_TAB)" header-class="bg-light">
								<template #header>
									Manufacture <span v-if="isColumnMandatoryFn('manufacture', 'emcr_manufacturers', MANUFACTURE_TAB)" class="text-danger">*</span>
								</template>
								<template #body="{ data }">
									<SelectPrime v-model="data.manufacture"
										size="small"
										:options="dialog.options.emcr_manufacturers"
										option-label="label"
										option-value="value"
										placeholder="Select Manufacture" filter
										panel-class="standard-desc-panel"
										:disabled="!isColumnEnabledFn('manufacture', 'emcr_manufacturers', MANUFACTURE_TAB) || dialog.loading.emcr_manufacturers"
										:invalid="isColumnInvalidFn(data, 'manufacture')"
										class="w-100"
										@show="onFieldOptionShow('emcr_manufacturers', MANUFACTURE_TAB, { column: 'manufacture', search: '' })"
										@filter="searchSelectFieldOptions($event, 'emcr_manufacturers', { tab: MANUFACTURE_TAB, column: 'manufacture' })"
									>
										<template #emptyfilter>
											<div v-if="dialog.loading.emcr_manufacturers" class="d-flex align-items-center justify-content-center gap-2 py-2">
												<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
											</div>
											<div v-else class="py-1 text-muted text-center">No options found</div>
										</template>

										<template #value="{ value, placeholder }">
											<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.emcr_manufacturers, value) }}</span>
											<span v-else>{{ placeholder }}</span>
										</template>

										<template #empty>
											<div v-if="dialog.loading.emcr_manufacturers" class="d-flex align-items-center justify-content-center gap-2 py-2">
												<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
											</div>
											<div v-else class="py-1 text-muted text-center">No options found</div>
										</template>
									</SelectPrime>
								</template>
							</Column>

							<Column v-if="isColumnVisibleFn('manufacture_ref', 'emcr_manufacturers', MANUFACTURE_TAB)" header-class="bg-light">
								<template #header>
									Type/Model/Part Number <span v-if="isColumnMandatoryFn('manufacture_ref', 'emcr_manufacturers', MANUFACTURE_TAB)" class="text-danger">*</span>
								</template>
								<template #body="{ data }">
									<InputText :model-value="data.manufacture_ref" size="small" placeholder="Enter Type/Model/Part Number"
										:disabled="!isColumnEnabledFn('manufacture_ref', 'emcr_manufacturers', MANUFACTURE_TAB)"
										:invalid="isColumnInvalidFn(data, 'manufacture_ref')" class="w-100"
										@update:model-value="(val) => data.manufacture_ref = (val || '').toUpperCase()" />
								</template>
							</Column>

							<Column v-if="isColumnVisibleFn('notes', 'emcr_manufacturers', MANUFACTURE_TAB)" header="Notes" header-class="bg-light">
								<template #body="{ data }">
									<InputText v-model="data.notes" size="small" placeholder="Enter Notes"
										:disabled="!isColumnEnabledFn('notes', 'emcr_manufacturers', MANUFACTURE_TAB)" class="w-100" />
								</template>
							</Column>

							<Column header-class="bg-light text-center" body-class="text-center" :header-style="{ width: '90px' }" :body-style="{ width: '90px' }">
								<template #header>#</template>
								<template #body="{ index }">
									<div class="d-flex align-items-center justify-content-center gap-2">
										<ButtonPrime v-if="!readonly" icon="pi pi-plus" size="small" outlined rounded severity="primary" @click="addManufacturerRow" />
										<ButtonPrime v-if="!readonly && dialog.form.emcr_manufacturers.length > 1" icon="pi pi-trash" outlined rounded
											size="small" severity="danger" @click="removeManufacturerRow(index)" />
									</div>
								</template>
							</Column>
						</DataTable>
					</div>
				</div>

				<div v-if="isTabVisibleFn(MRP_TAB)" class="card mb-2 mt-3" :class="{ 'border-danger': dialog.errors.mrp }">
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('mrp')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">MRP</span>
								<span v-if="isSectionMandatoryFn('MRP')" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.mrp" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.mrp }}
							</small>
						</div>

						<i :class="dialog.sections.mrp ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.mrp" class="card-body pt-0">
						<div class="row g-3">
							<div v-if="isFieldVisibleFn('sparepart_category', MRP_TAB)" class="col-md-6 mb-2">
								<label for="spare-part-category" class="form-label fw-semibold">
									Spare Part Category <span v-if="isFieldMandatoryFn('sparepart_category', MRP_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.sparepart_category"
									size="small"
									:options="dialog.options.sparepart_category"
									option-label="label"
									option-value="value"
									placeholder="Select Spare Part Category" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('sparepart_category', MRP_TAB) || !isFieldDependencyReadyFn('sparepart_category', MRP_TAB) || dialog.loading.sparepart_category"
									:invalid="isFieldInvalidFn('sparepart_category')" class="w-100"
									@show="onFieldOptionShow('sparepart_category', MRP_TAB)"
									@change="onFieldChangeWithAutofill('sparepart_category', MRP_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.sparepart_category" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.sparepart_category, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.sparepart_category" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('mrp_type', MRP_TAB)" class="col-md-6 mb-2">
								<label for="mrp-type" class="form-label fw-semibold">
									MRP Type <span v-if="isFieldMandatoryFn('mrp_type', MRP_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.mrp_type"
									size="small"
									:options="dialog.options.mrp_type"
									option-label="label"
									option-value="value"
									placeholder="Select MRP Type" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('mrp_type', MRP_TAB) || !isFieldDependencyReadyFn('mrp_type', MRP_TAB) || dialog.loading.mrp_type"
									:invalid="isFieldInvalidFn('mrp_type')" class="w-100"
									@show="onFieldOptionShow('mrp_type', MRP_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.mrp_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.mrp_type, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.mrp_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('lot_size', MRP_TAB)" class="col-md-6 mb-2">
								<label for="lot-size" class="form-label fw-semibold">
									Lot Size <span v-if="isFieldMandatoryFn('lot_size', MRP_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.lot_size"
									size="small"
									:options="dialog.options.lot_size"
									option-label="label"
									option-value="value"
									placeholder="Select Lot Size" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('lot_size', MRP_TAB) || !isFieldDependencyReadyFn('lot_size', MRP_TAB) || dialog.loading.lot_size"
									:invalid="isFieldInvalidFn('lot_size')" class="w-100"
									@show="onFieldOptionShow('lot_size', MRP_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.lot_size" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.lot_size, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.lot_size" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('reorder_point', MRP_TAB)" class="col-md-6 mb-2">
								<label for="reorder-point" class="form-label fw-semibold">
									Reorder Point <span v-if="isFieldMandatoryFn('reorder_point', MRP_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="reorder-point" v-model="dialog.form.reorder_point" size="small" placeholder="Enter Reorder Point"
									:disabled="!isFieldEnabledFn('reorder_point', MRP_TAB)" :invalid="isFieldInvalidFn('reorder_point')" fluid />
							</div>

							<div v-if="isFieldVisibleFn('safety_stock', MRP_TAB)" class="col-md-6 mb-2">
								<label for="safety-stock" class="form-label fw-semibold">
									Safety Stock <span v-if="isFieldMandatoryFn('safety_stock', MRP_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="safety-stock" v-model="dialog.form.safety_stock" size="small" placeholder="Enter Safety Stock"
									:disabled="!isFieldEnabledFn('safety_stock', MRP_TAB)" :invalid="isFieldInvalidFn('safety_stock')" fluid />
							</div>

							<div v-if="isFieldVisibleFn('abc_indicator', MRP_TAB)" class="col-md-6 mb-2">
								<label for="abc-indicator" class="form-label fw-semibold">
									ABC Indicator <span v-if="isFieldMandatoryFn('abc_indicator', MRP_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.abc_indicator"
									size="small"
									:options="dialog.options.abc_indicator"
									option-label="label"
									option-value="value"
									placeholder="Select ABC Indicator" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('abc_indicator', MRP_TAB) || dialog.loading.abc_indicator"
									:invalid="isFieldInvalidFn('abc_indicator')" class="w-100"
									@show="onFieldOptionShow('abc_indicator', MRP_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.abc_indicator" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.abc_indicator, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.abc_indicator" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('maximum_stock_level', MRP_TAB)" class="col-md-6 mb-2">
								<label for="maximum-stock-level" class="form-label fw-semibold">
									Maximum Stock Level <span v-if="isFieldMandatoryFn('maximum_stock_level', MRP_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="maximum-stock-level" v-model="dialog.form.maximum_stock_level" size="small" placeholder="Enter Maximum Stock Level"
									:disabled="!isFieldEnabledFn('maximum_stock_level', MRP_TAB)" :invalid="isFieldInvalidFn('maximum_stock_level')" fluid />
							</div>

							<div v-if="isFieldVisibleFn('rounding_value', MRP_TAB)" class="col-md-6 mb-2">
								<label for="rounding-value" class="form-label fw-semibold">
									Rounding Value <span v-if="isFieldMandatoryFn('rounding_value', MRP_TAB)" class="text-danger">*</span>
								</label>
								<InputNumber id="rounding-value" v-model="dialog.form.rounding_value" size="small" placeholder="Enter Rounding Value"
									:disabled="!isFieldEnabledFn('rounding_value', MRP_TAB)" :invalid="isFieldInvalidFn('rounding_value')" fluid />
							</div>
						</div>
					</div>
				</div>

				<div v-if="isTabVisibleFn(ATTACHMENT_TAB)" class="card mb-2 mt-3" :class="{ 'border-danger': dialog.errors.attachment }">
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('attachment')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">Attachment Document</span>
								<span v-if="isSectionMandatoryFn(ATTACHMENT_TAB)" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.attachment" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.attachment }}
							</small>
						</div>

						<i :class="dialog.sections.attachment ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.attachment" class="card-body pt-0">
						<div class="d-flex justify-content-end mb-2">
							<input ref="attachmentInput" type="file" multiple style="display:none"
								:accept="attachmentAcceptAttr"
								@change="onAttachmentSelected" />
							<button type="button" class="mx-1 mb-0 btn btn-sm btn-dark" :disabled="!isFieldEnabledFn('emcr_attachments', ATTACHMENT_TAB)"
								@click="openAttachmentPicker">Add Files</button>
						</div>

						<DataTable :value="dialog.form.emcr_attachments" class="p-datatable-sm p-datatable-gridlines" scrollable>
							<Column header="File Name" header-class="bg-light">
								<template #body="{ data }">
									{{ data.name || data.file_code || '' }}
								</template>
							</Column>

							<Column header="Size" header-class="bg-light" :header-style="{ width: '120px' }" :body-style="{ width: '120px' }">
								<template #body="{ data }">
									{{ formatAttachmentSize(data) }}
								</template>
							</Column>

							<Column header="Actions" header-class="bg-light text-center" body-class="text-center" :header-style="{ width: '150px' }" :body-style="{ width: '150px' }">
								<template #body="{ data, index }">
									<ButtonPrime v-if="isPreviewableAttachment(data)" v-tooltip.bottom="'Preview'"
										icon="pi pi-eye" outlined rounded severity="info" size="small" class="me-1"
										@click="previewAttachment(data)" />
									<ButtonPrime v-if="isDownloadableAttachment(data)" v-tooltip.bottom="'Download'"
										icon="pi pi-download" outlined rounded severity="secondary" size="small" class="me-1"
										@click="downloadAttachment(data)" />
									<ButtonPrime icon="pi pi-trash" outlined rounded size="small" severity="danger"
										:disabled="!isFieldEnabledFn('emcr_attachments', ATTACHMENT_TAB)"
										@click="removeAttachmentRow(index)" />
								</template>
							</Column>

							<template #empty>
								<div class="py-1 text-muted">No Attachments</div>
							</template>
						</DataTable>
					</div>
				</div>

				<div v-if="isTabVisibleFn(EXTEND_DATA_TAB)" class="card mb-2 mt-3" :class="{ 'border-danger': dialog.errors.organizationLevel }">
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('orgalizationLevel')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">Extend Data</span>
								<span v-if="isSectionMandatoryFn(EXTEND_DATA_TAB)" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.organizationLevel" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.organizationLevel }}
							</small>
						</div>

						<i :class="dialog.sections.orgalizationLevel ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.orgalizationLevel" class="card-body pt-0">
						<div class="row g-3">
						<div class="col-md-6 border-end pe-md-4">
							<div class="mb-2 pb-1 border-bottom">
								<span class="fw-semibold" style="font-size: 0.85rem;">Extend to</span>
							</div>
					<div v-if="isFieldVisibleFn('ext_plant', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-plant" class="form-label fw-semibold">
							Plant <span v-if="isFieldMandatoryFn('ext_plant', EXTEND_DATA_TAB)" class="text-danger">*</span>
						</label>

						<SelectPrime v-model="dialog.form.ext_plant"
							size="small"
							:options="dialog.options.ext_plant"
							option-label="label"
							option-value="value"
							placeholder="Select Plant" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_plant', EXTEND_DATA_TAB) || dialog.loading.ext_plant"
							:invalid="isFieldInvalidFn('ext_plant')" class="w-100"
							@show="onFieldOptionShow('ext_plant', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_plant, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_location', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-location" class="form-label fw-semibold">
							Location
						</label>

						<SelectPrime v-model="dialog.form.ext_location"
							size="small"
							:options="dialog.options.ext_location"
							option-label="label"
							option-value="value"
							placeholder="Select Location" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_location', EXTEND_DATA_TAB) || dialog.loading.ext_location"
							:invalid="isFieldInvalidFn('ext_location')" class="w-100"
							@show="onFieldOptionShow('ext_location', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_location" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_location, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_location" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_valuation_type', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-valuation-type" class="form-label fw-semibold">
							Valuation Type
						</label>

						<SelectPrime v-model="dialog.form.ext_valuation_type"
							size="small"
							:options="dialog.options.ext_valuation_type"
							option-label="label"
							option-value="value"
							placeholder="Select Valuation Type" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_valuation_type', EXTEND_DATA_TAB) || dialog.loading.ext_valuation_type"
							:invalid="isFieldInvalidFn('ext_valuation_type')" class="w-100"
							@show="onFieldOptionShow('ext_valuation_type', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_valuation_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_valuation_type, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_valuation_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_sales_org', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-sales-org" class="form-label fw-semibold">
							Sales Organization
						</label>

						<SelectPrime v-model="dialog.form.ext_sales_org"
							size="small"
							:options="dialog.options.ext_sales_org"
							option-label="label"
							option-value="value"
							placeholder="Select Sales Organization" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_sales_org', EXTEND_DATA_TAB) || dialog.loading.ext_sales_org"
							:invalid="isFieldInvalidFn('ext_sales_org')" class="w-100"
							@show="onFieldOptionShow('ext_sales_org', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_sales_org" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_sales_org, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_sales_org" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_dist_channel', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-dist-channel" class="form-label fw-semibold">
							Distribution Channel
						</label>

						<SelectPrime v-model="dialog.form.ext_dist_channel"
							size="small"
							:options="dialog.options.ext_dist_channel"
							option-label="label"
							option-value="value"
							placeholder="Select Distribution Channel" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_dist_channel', EXTEND_DATA_TAB) || dialog.loading.ext_dist_channel"
							:invalid="isFieldInvalidFn('ext_dist_channel')" class="w-100"
							@show="onFieldOptionShow('ext_dist_channel', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_dist_channel" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_dist_channel, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_dist_channel" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_warehouse_number', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-warehouse-number" class="form-label fw-semibold">
							Warehouse Number
						</label>

						<SelectPrime v-model="dialog.form.ext_warehouse_number"
							size="small"
							:options="dialog.options.ext_warehouse_number"
							option-label="label"
							option-value="value"
							placeholder="Select Warehouse Number" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_warehouse_number', EXTEND_DATA_TAB) || dialog.loading.ext_warehouse_number"
							:invalid="isFieldInvalidFn('ext_warehouse_number')" class="w-100"
							@show="onFieldOptionShow('ext_warehouse_number', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_warehouse_number" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_warehouse_number, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_warehouse_number" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_storage_type', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-storage-type" class="form-label fw-semibold">
							Storage Type
						</label>

						<SelectPrime v-model="dialog.form.ext_storage_type"
							size="small"
							:options="dialog.options.ext_storage_type"
							option-label="label"
							option-value="value"
							placeholder="Select Storage Type" filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_storage_type', EXTEND_DATA_TAB) || dialog.loading.ext_storage_type"
							:invalid="isFieldInvalidFn('ext_storage_type')" class="w-100"
							@show="onFieldOptionShow('ext_storage_type', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_storage_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_storage_type, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_storage_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

						</div>

						<div class="col-md-6 ps-md-4">
							<div class="mb-2 pb-1 border-bottom">
								<span class="fw-semibold" style="font-size: 0.85rem;">Copy From</span>
							</div>
					<div v-if="isFieldVisibleFn('ext_cf_plant', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-plant" class="form-label fw-semibold">
							Plant
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_plant"
							size="small"
							:options="dialog.options.ext_cf_plant"
							option-label="label"
							option-value="value"
							placeholder="Select Plant from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_plant', EXTEND_DATA_TAB) || dialog.loading.ext_cf_plant"
							:invalid="isFieldInvalidFn('ext_cf_plant')" class="w-100"
							@show="onFieldOptionShow('ext_cf_plant', EXTEND_DATA_TAB)"
							@change="onFieldDependencyChange('ext_cf_plant', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_plant, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_plant" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_location', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-location" class="form-label fw-semibold">
							Location
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_location"
							size="small"
							:options="dialog.options.ext_cf_location"
							option-label="label"
							option-value="value"
							placeholder="Select Location from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_location', EXTEND_DATA_TAB) || !isFieldDependencyReadyFn('ext_cf_location', EXTEND_DATA_TAB) || dialog.loading.ext_cf_location"
							:invalid="isFieldInvalidFn('ext_cf_location')" class="w-100"
							@show="onFieldOptionShow('ext_cf_location', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_location" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_location, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_location" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_valuation_type', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-valuation-type" class="form-label fw-semibold">
							Valuation Type
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_valuation_type"
							size="small"
							:options="dialog.options.ext_cf_valuation_type"
							option-label="label"
							option-value="value"
							placeholder="Select Valuation Type from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_valuation_type', EXTEND_DATA_TAB) || !isFieldDependencyReadyFn('ext_cf_valuation_type', EXTEND_DATA_TAB) || dialog.loading.ext_cf_valuation_type"
							:invalid="isFieldInvalidFn('ext_cf_valuation_type')" class="w-100"
							@show="onFieldOptionShow('ext_cf_valuation_type', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_valuation_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_valuation_type, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_valuation_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_sales_org', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-sales-org" class="form-label fw-semibold">
							Sales Organization
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_sales_org"
							size="small"
							:options="dialog.options.ext_cf_sales_org"
							option-label="label"
							option-value="value"
							placeholder="Select Sales Organization from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_sales_org', EXTEND_DATA_TAB) || dialog.loading.ext_cf_sales_org"
							:invalid="isFieldInvalidFn('ext_cf_sales_org')" class="w-100"
							@show="onFieldOptionShow('ext_cf_sales_org', EXTEND_DATA_TAB)"
							@change="onFieldDependencyChange('ext_cf_sales_org', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_sales_org" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_sales_org, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_sales_org" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_dist_channel', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-dist-channel" class="form-label fw-semibold">
							Distribution Channel
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_dist_channel"
							size="small"
							:options="dialog.options.ext_cf_dist_channel"
							option-label="label"
							option-value="value"
							placeholder="Select Distribution Channel from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_dist_channel', EXTEND_DATA_TAB) || !isFieldDependencyReadyFn('ext_cf_dist_channel', EXTEND_DATA_TAB) || dialog.loading.ext_cf_dist_channel"
							:invalid="isFieldInvalidFn('ext_cf_dist_channel')" class="w-100"
							@show="onFieldOptionShow('ext_cf_dist_channel', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_dist_channel" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_dist_channel, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_dist_channel" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_warehouse_number', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-warehouse-number" class="form-label fw-semibold">
							Warehouse Number
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_warehouse_number"
							size="small"
							:options="dialog.options.ext_cf_warehouse_number"
							option-label="label"
							option-value="value"
							placeholder="Select Warehouse Number from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_warehouse_number', EXTEND_DATA_TAB) || !isFieldDependencyReadyFn('ext_cf_warehouse_number', EXTEND_DATA_TAB) || dialog.loading.ext_cf_warehouse_number"
							:invalid="isFieldInvalidFn('ext_cf_warehouse_number')" class="w-100"
							@show="onFieldOptionShow('ext_cf_warehouse_number', EXTEND_DATA_TAB)"
							@change="onFieldDependencyChange('ext_cf_warehouse_number', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_warehouse_number" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_warehouse_number, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_warehouse_number" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

					<div v-if="isFieldVisibleFn('ext_cf_storage_type', EXTEND_DATA_TAB)" class="mb-2">
						<label for="ext-cf-storage-type" class="form-label fw-semibold">
							Storage Type
						</label>

						<SelectPrime v-model="dialog.form.ext_cf_storage_type"
							size="small"
							:options="dialog.options.ext_cf_storage_type"
							option-label="label"
							option-value="value"
							placeholder="Select Storage Type from Material..." filter
							panel-class="standard-desc-panel"
							:disabled="!isFieldEnabledFn('ext_cf_storage_type', EXTEND_DATA_TAB) || !isFieldDependencyReadyFn('ext_cf_storage_type', EXTEND_DATA_TAB) || dialog.loading.ext_cf_storage_type"
							:invalid="isFieldInvalidFn('ext_cf_storage_type')" class="w-100"
							@show="onFieldOptionShow('ext_cf_storage_type', EXTEND_DATA_TAB)"
						>
							<template #emptyfilter>
								<div v-if="dialog.loading.ext_cf_storage_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>

							<template #value="{ value, placeholder }">
								<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.ext_cf_storage_type, value) }}</span>
								<span v-else>{{ placeholder }}</span>
							</template>

							<template #empty>
								<div v-if="dialog.loading.ext_cf_storage_type" class="d-flex align-items-center justify-content-center gap-2 py-2">
									<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
								</div>
								<div v-else class="py-1 text-muted text-center">No options found</div>
							</template>
						</SelectPrime>
					</div>

						</div>
						</div>
					</div>
				</div>

				<div v-if="isTabVisibleFn(ACCOUNTING_TAB)" class="card mb-2 mt-3" :class="{ 'border-danger': dialog.errors.accounting }">
					<div class="card-header py-1 d-flex mt-2 mb-2 justify-content-between align-items-center" role="button" @click="toggleSection('accounting')">
						<div class="d-flex align-items-center flex-wrap gap-2">
							<div class="d-flex align-items-center gap-1">
								<span class="fw-semibold">Accounting</span>
								<span v-if="isSectionMandatoryFn(ACCOUNTING_TAB)" class="text-danger">*</span>
							</div>
							<small v-if="dialog.errors.accounting" class="text-danger d-flex align-items-center"
								style="font-style: italic; font-size: 0.75rem;">
								<i class="fas fa-exclamation-circle me-1"></i> {{ dialog.errors.accounting }}
							</small>
						</div>

						<i :class="dialog.sections.accounting ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
					</div>

					<div v-if="dialog.sections.accounting" class="card-body pt-0">
						<div class="row g-3">
							<div v-if="isFieldVisibleFn('valuation_class', ACCOUNTING_TAB)" class="col-md-4 mb-2">
								<label for="accounting-valuation-class" class="form-label fw-semibold">
									Valuation Class <span v-if="isFieldMandatoryFn('valuation_class', ACCOUNTING_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.valuation_class"
									size="small"
									:options="dialog.options.valuation_class"
									option-label="label"
									option-value="value"
									placeholder="Select Valuation Class" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('valuation_class', ACCOUNTING_TAB) || dialog.loading.valuation_class"
									:invalid="isFieldInvalidFn('valuation_class')" class="w-100"
									@show="onFieldOptionShow('valuation_class', ACCOUNTING_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.valuation_class" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.valuation_class, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.valuation_class" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('price_control', ACCOUNTING_TAB)" class="col-md-4 mb-2">
								<label for="price-control" class="form-label fw-semibold">
									Price Control <span v-if="isFieldMandatoryFn('price_control', ACCOUNTING_TAB)" class="text-danger">*</span>
								</label>

								<SelectPrime v-model="dialog.form.price_control"
									size="small"
									:options="dialog.options.price_control"
									option-label="label"
									option-value="value"
									placeholder="Select Price Control" filter
									panel-class="standard-desc-panel"
									:disabled="!isFieldEnabledFn('price_control', ACCOUNTING_TAB) || dialog.loading.price_control"
									:invalid="isFieldInvalidFn('price_control')" class="w-100"
									@show="onFieldOptionShow('price_control', ACCOUNTING_TAB)"
								>
									<template #emptyfilter>
										<div v-if="dialog.loading.price_control" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>

									<template #value="{ value, placeholder }">
										<span v-if="value != null && value !== ''" class="select-value-label">{{ getSelectValueLabelFn(dialog.options.price_control, value) }}</span>
										<span v-else>{{ placeholder }}</span>
									</template>

									<template #empty>
										<div v-if="dialog.loading.price_control" class="d-flex align-items-center justify-content-center gap-2 py-2">
											<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>
										</div>
										<div v-else class="py-1 text-muted text-center">No options found</div>
									</template>
								</SelectPrime>
							</div>

							<div v-if="isFieldVisibleFn('std_price', ACCOUNTING_TAB)" class="col-md-4 mb-2">
								<label for="standard-price" class="form-label fw-semibold">
									Standard Price <span v-if="isFieldMandatoryFn('std_price', ACCOUNTING_TAB)" class="text-danger">*</span>
								</label>

								<InputNumber id="standard-price" v-model="dialog.form.std_price" size="small" placeholder="Enter Standard Price"
									:disabled="!isFieldEnabledFn('std_price', ACCOUNTING_TAB)" :invalid="isFieldInvalidFn('std_price')" fluid />
							</div>
						</div>
					</div>
				</div>
			</div>
		</component>

		<!-- Dialog Preview PDF (Attachment Document) -->
		<DialogPrime v-model:visible="previewModal.visible" modal :header="previewModal.title"
			:style="{ width: '95vw', maxHeight: '95vh', margin: '10px' }">
			<iframe v-if="previewModal.pdfUrl" :src="previewModal.pdfUrl + '#zoom=150'" class="pdf-iframe"></iframe>
		</DialogPrime>

		<!-- Native Image Preview Trigger (Hidden) -->
		<div style="position: absolute; width: 0; height: 0; overflow: hidden;">
			<PrimeImage ref="imagePreviewer" :src="previewModal.imageUrl" preview @load="onPreviewImageLoad" />
		</div>
	</div>
</template>

<script>
	import api from "@/helpers/api";
	import advanceSearchService from "@/services/generalLayout/advanceSearchService";
	import materialNumberFormatter from "@/helpers/materialNumberFormatter";
	import formConfig from '@/helpers/New_formConfig';

	// Field yang dirender InputNumber (lihat normalizeNumberFields).
	const NUMBER_INPUT_FIELDS = ['price_unit', 'estimated_annual_usage', 'reorder_point', 'safety_stock', 'maximum_stock_level', 'rounding_value', 'std_price'];
	import { getCurrentInstance } from 'vue';
	import { useFormConfigEngine } from '@/composables/useFormConfigEngine';
	import smartcatAttachmentService from '@/services/dictionary/smartcatAttachmentService';
	import userFormService from '@/services/materialCatalogingRequest/New_userFormService';
	import requestListService from '@/services/materialCatalogingRequest/requestListService';

	const BASIC_DATA_TAB = 'Basic Data';
	const MANUFACTURE_TAB = 'Manufacture';
	const MRP_TAB = 'MRP';
	const ATTACHMENT_TAB = 'Attachment Document';
	const EXTEND_DATA_TAB = 'Extend Data';
	const ACCOUNTING_TAB = 'Accounting';

	// Dipakai preloadSelectedFieldLabels/resolveSelectedFieldLabel supaya resolve label field ini fetch 1 value yang match (search=value), bukan preload semua list.
	const SEARCH_AS_YOU_TYPE_FIELDS = new Set(['equipment']);

	const ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024; // 10MB

	// Mapping tab -> key error/section dipakai validateForm() buat nge-highlight card & auto-expand
	// section yang lagi collapsed. `sectionKey` beda ejaan dengan `errorKey` buat Extend Data
	// ("orgalizationLevel" vs "organizationLevel") -- sengaja disamakan ke typo yang SUDAH dipakai
	// di dialog.sections/toggleSection existing, bukan typo baru.
	const TAB_SECTION_META = {
		[BASIC_DATA_TAB]: { errorKey: 'basicData', sectionKey: 'basicData' },
		[MANUFACTURE_TAB]: { errorKey: 'manufacturerPartNumber', sectionKey: 'manufacturerPartNumber' },
		[MRP_TAB]: { errorKey: 'mrp', sectionKey: 'mrp' },
		[ATTACHMENT_TAB]: { errorKey: 'attachment', sectionKey: 'attachment' },
		[EXTEND_DATA_TAB]: { errorKey: 'organizationLevel', sectionKey: 'orgalizationLevel' },
		[ACCOUNTING_TAB]: { errorKey: 'accounting', sectionKey: 'accounting' },
	};

	export default {
		props: {
			formConfig: { type: Object, default: null },
			mcrType: { type: String, default: '' },
			// viewData berisi data table emcrs & emcr_items
			viewData: { type: Object, default: null },
			readonly: { type: Boolean, default: false },
			// Render isi form langsung di halaman (bukan modal) -- dipakai tab User Request New_CataloguerForm (bersama `readonly`).
			inline: { type: Boolean, default: false }
		},
		emits: ['saved', 'close'],
		expose: ['openForAdd', 'openForEdit'],
		setup(props) {
			// `vm` dibaca lazy lewat getter -- data() (dialog, materialLookupDetail) baru ada setelah setup() jalan.
			const vm = getCurrentInstance().proxy;
			return useFormConfigEngine({
				vm,
				getFormConfig: () => props.formConfig,
				getDialog: () => vm.dialog,
				getMcrType: () => props.mcrType,
				isView: () => props.readonly,
				fieldResolvers: {
					// Cuma dianggap "terisi" setelah user benar-benar PILIH suggestion (isMaterialSelected), bukan
					// sekadar lagi ngetik/nyari -- supaya enabled/depends_on/trigger yang gantung ke material_number
					// tidak aktif duluan pas baru ketik sebagian nomor (lihat DECISIONS_LOG.md).
					material_number: () => (vm.dialog.isMaterialSelected ? vm.dialog.form.material_number : ''),
					// Plant itu multi-select, sedangkan mesin config cuma kenal satu nilai (filter default_value
					// lookup, query param data_source, kondisi in/equals) -> dipakai plant PERTAMA. Daftar
					// lengkapnya tetap dikirim ke BE lewat plant[i] (lihat primaryPlant()).
					plant: () => vm.primaryPlant(),
				},
				getDetailSources: () => vm.detailArraySources,
				searchAsYouTypeFields: SEARCH_AS_YOU_TYPE_FIELDS,
			});
		},
		data() {
			return {
				BASIC_DATA_TAB,
				MANUFACTURE_TAB,
				MRP_TAB,
				ATTACHMENT_TAB,
				EXTEND_DATA_TAB,
				ACCOUNTING_TAB,
				dialog: {
					internalVisible: false,
					saving: false,
					validated: false,
					errors: {},
					editingItemId: null,
					isMaterialSelected: false,
					form: this.emptyRow(),
					sections: { basicData: true, manufacturerPartNumber: true, mrp: true, attachment: true, orgalizationLevel: true, accounting: true },

					// state untuk setiap field
					options: {},
					loading: {},
					loaded: {}
				},
				// Cache hasil fetch material detail (dari onMaterialNumberSelect), dipakai ulang saat trigger lain (mis. plant, buat valuation_class) baru terisi belakangan, tanpa fetch API lagi.
				materialLookupDetail: null,
				previewModal: {
					visible: false,
					title: '',
					pdfUrl: '',
					imageUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
				},
				// Snapshot enabled TERAKHIR per field (bukan cuma current state), dipakai clearDisabledFieldsWithoutDefault() buat deteksi TRANSISI enabled(true) -> enabled(false). Wajib pakai histori, bukan cuma baca isFieldEnabledFn saat itu juga, supaya field yang MEMANG dari awal disabled (baru buka form/baru selesai hydrate edit) tidak ikut kereset.
				enabledFieldSnapshot: {},
				// Nilai terakhir field sumber auto_select_single (lihat onAutomationSourceChange).
				automationSnapshot: {}
			};
		},
		watch: {
			// Plant multi-select: yang berubah bisa beberapa plant sekaligus, jadi dibandingkan sebagai daftar.				// Default lookup (mis. MRP dari SAP) dihitung ulang mengikuti plant utama (plant pertama).
				// MANUFACTURE_TAB ikut: trigger default kolomnya [material_number, plant].
				'dialog.form.plant'(newVal, oldVal) {
					if (JSON.stringify(newVal || []) === JSON.stringify(oldVal || [])) return;
					if (!formConfig.isFilledValue(newVal)) return;
					// MRP_TAB sengaja TIDAK ikut: nilai MRP dari SAP diisi sekali saat Material Number dipilih (fetchMaterialLookupDetail).
					// Plant diganti user -> INALUM: formasi ConfigDataMapping (onAutomationSourceChange), ANTAM: nilai MRP dibiarkan.
					this.applyLookupDefaults([BASIC_DATA_TAB, MANUFACTURE_TAB]);
					this.normalizeNumberFields();
				},
			// ANTAM MCR003 (Extend): semua item satu MCR wajib Plant tujuan (ext_plant) yang sama dengan item yang sudah disimpan.
			// Item yang sedang diedit tidak dihitung (boleh mengganti plant-nya sendiri kalau item lain belum ada). Beda -> popup &
			// Plant dikosongkan. BE menegakkan aturan yang sama (UserFormService::assertSameExtPlantForAntamExtend).
			'dialog.form.ext_plant'(val) {
				if (!val || this.readonly) return;
				const header = this.viewData || {};
				if (String(header.company || '').toUpperCase() !== 'ANTAM' || String(header.type || '').toUpperCase() !== 'MCR003') return;
				const required = (header.emcr_item || [])
					.filter((item) => item.id !== this.dialog.editingItemId && formConfig.isFilledValue(item.ext_plant))
					.map((item) => item.ext_plant)[0];
				if (!required || String(required) === String(val)) return;
				this.$nextTick(() => {
					this.dialog.form.ext_plant = '';
				});
				this.$swal({
					icon: 'warning',
					title: 'Warning',
					text: 'You cannot add a different plant in this MCR!',
					confirmButtonText: 'Close',
				});
			},
			'dialog.form.ext_cf_plant'(newVal, oldVal) {
				if (newVal === oldVal || !newVal) return;
				this.applyLookupDefaults([ACCOUNTING_TAB]);
				this.normalizeNumberFields();
			},
			'previewModal.visible'(val) {
				if (!val && this.previewModal.pdfUrl) {
					URL.revokeObjectURL(this.previewModal.pdfUrl);
					this.previewModal.pdfUrl = '';
				}
			},
			// Untuk triger clear field yang tidak punya default value ketika tadinya enable jadi disabled, misal rubah material type dari ZFGD jadi ZSPR
			'dialog.form': {
				deep: true,
				handler() {
					this.clearDisabledFieldsWithoutDefault();
				}
			},
			// Cek material type untuk validasi supaya hanya bisa single material type,
			// misal sudah pilih ZSPR tidak bisa menambah item dengan material type lain
			'dialog.form.material_type'(val) {
				if (!val) return;

				const items = this.viewData?.emcr_item || [];
				if (!items.length) return;

				// SEMENTARA hardcode
				const materialTypeGroups = [
					['ZNNV'],['ZSPR','ZNOV','ZFGD','ZSFG','ZRAW','ZSUP']
				];

				const groupOf = (type) => (materialTypeGroups.find(g => g.includes(type)) || [type]).join('|');
				// Aturan satu kelompok material type per MCR cuma INALUM (ANTAM & TIMAH tidak; ANTAM MCR003 punya aturan plant
				// sama, lihat watcher ext_plant). Company dari header MCR (viewData = emcrs).
				const allowed = String(this.viewData?.company || '').toUpperCase() !== 'INALUM'
					|| items.every(item => groupOf(item.material_type) === groupOf(val));

				if (!allowed) {
					this.$nextTick(() => {
						this.dialog.form = this.emptyRow();
					});
					this.$swal({
						icon: 'warning',
						title: 'Warning',
						text: 'You cannot add a different material type in this MCR!',
						confirmButtonText: 'Close',
					});
				}

				// Plant cuma multi-select untuk ZFGD: kalau material type pindah ke selain itu, daftar plant
				// disisakan plant pertama supaya sama dengan yang tampil di form (single select).
				if (!this.plantIsMultiple && (this.dialog.form.plant || []).length > 1) {
					this.dialog.form.plant = [this.primaryPlant()];
				}
				// Opsi yang dibatasi per Material Type (`option_values`, mis. Plant INALUM ZFGD tanpa A002/A0N2):
				// plant terpilih yang tidak lagi diizinkan dibuang.
				this.pruneDisallowedFieldValues([BASIC_DATA_TAB, EXTEND_DATA_TAB]);
			}
		},
		computed: {
			// Plant multi-select cuma untuk material type tertentu (config FORM01: `multiple` = ZFGD).
			plantIsMultiple() {
				return formConfig.isFieldMultiple(this.formConfig, BASIC_DATA_TAB, 'plant', this.formConfigCtx);
			},
			// SelectPrime (satu pilihan) butuh nilai scalar, sedangkan state form selalu array (lihat
			// primaryPlant()) -- jembatan get/set supaya dua-duanya pakai `dialog.form.plant`.
			plantSingleModel: {
				get() {
					return this.primaryPlant();
				},
				set(value) {
					this.dialog.form.plant = value ? [value] : [];
				},
			},
			// Atribut pembungkus form: modal (default, sama persis dengan binding DialogPrime sebelumnya) atau div biasa (`inline`).
			wrapperAttrs() {
				if (this.inline) return { class: 'mcr-item-form-inline' };
				return {
					visible: this.dialog.internalVisible,
					'onUpdate:visible': (val) => { this.dialog.internalVisible = val; },
					modal: true,
					draggable: false,
					style: 'width: 70rem; margin: 10px;',
					class: 'add-item-modal'
				};
			},
			detailArraySources() {
				return {
					materialLookupDetail: this.materialLookupDetail
				};
			},
			// `accept` attribute input file attachment -- diturunkan dari row_rules `mimes` di form
			// config, SATU sumber kebenaran yang sama dengan validasi di onAttachmentSelected() & BE.
			attachmentAcceptAttr() {
				const allowedExt = formConfig.getRowRuleValue(this.formConfig, ATTACHMENT_TAB, 'emcr_attachments', 'mimes') || [];
				return allowedExt.map(ext => `.${ext}`).join(',');
			}
		},
		mounted() {},
		methods: {
			emptyRow() {
				return {
					// Basic Data Section
					mcr_code: this.viewData?.mcr_code || '',
					material_number: '',
					material_number_raw: '',
					material_type: '',
					material_group: '',
					item_type: '',
					// Plant multi-select: disimpan sebagai daftar pilihan, plant pertama = plant utama item.
					plant: [],
					uom: '',
					equipment: '',
					currency: '',
					price_reference: null,
					price_unit: null,
					service_group: '',
					service_category: '',
					specification: '',
					descriptions: '',
					notes: '',
					valuation_class: '',
					standard_description: '',
					estimated_annual_usage: null,
					repairable_item: null,
					equipments: [],
					emcr_attachments: [],

					// Manufacturers Section
					emcr_manufacturers: [{ old_manufacture: '', old_manufacture_ref: '', manufacture: '', manufacture_ref: '', notes: '' }],

					// MRP Section
					sparepart_category: '',
					mrp_type: '',
					lot_size: '',
					reorder_point: null,
					safety_stock: null,
					abc_indicator: '',
					maximum_stock_level: null,
					rounding_value: null,

					// Extend Data Section (Org Level)
					ext_plant: '',
					ext_location: '',
					ext_valuation_type: '',
					ext_sales_org: '',
					ext_dist_channel: '',
					ext_warehouse_number: '',
					ext_storage_type: '',

					// Extend Data Section (Copy From)
					ext_cf_plant: '',
					ext_cf_location: '',
					ext_cf_valuation_type: '',
					ext_cf_sales_org: '',
					ext_cf_dist_channel: '',
					ext_cf_warehouse_number: '',
					ext_cf_storage_type: '',

					// Accounting Section
					price_control: '',
					std_price: null,
				};
			},
			toggleSection(key) {
				this.dialog.sections[key] = !this.dialog.sections[key];
			},
			addManufacturerRow() {
				this.dialog.form.emcr_manufacturers.push({ old_manufacture: '', old_manufacture_ref: '', manufacture: '', manufacture_ref: '', notes: '' });
			},
			removeManufacturerRow(index) {
				if (this.dialog.form.emcr_manufacturers.length <= 1) return;
				this.dialog.form.emcr_manufacturers.splice(index, 1);
			},
			searchMaterialNumber(event) {
				// User lagi ngetik/nyari lagi -> bukan value yang sudah "dipilih" lagi.
				this.dialog.isMaterialSelected = false;
				const query = event.query?.trim();
				if (!query || query.length < 2) {
					this.dialog.options.material_number = [];
					return;
				}

				// Search-as-you-type: query yang diketik HARUS dikirim sebagai param `search`
				// (EuMaterialService cuma mengenali search + company; param `material_number`
				// di data_source tidak dipakai BE, dan nilainya memang '' karena belum dipilih).
				return this.loadSelectFieldOptions('material_number', {
					force: true,
					tab: BASIC_DATA_TAB,
					search: query,
					mapItem: (item) => ({
						label: materialNumberFormatter.formatMaterialNumber(item.material_number),
						value: materialNumberFormatter.formatMaterialNumber(item.material_number),
						raw: item.material_number,
						short_text: item.short_text || ''
					})
				});
			},
			async fetchMaterialLookupDetail(materialNumber) {
				this.resetDetailArrayFields([EXTEND_DATA_TAB]);

				if (!materialNumber) {
					this.materialLookupDetail = null;
					return;
				}
				try {
					const resp = await api(this, advanceSearchService.getMaterialDetail(materialNumber), false);
					this.materialLookupDetail = resp?.data || resp || null;
					// ATTACHMENT_TAB ikut: trigger default-nya cuma material_number -- attachment EU material
					// (eu_material_attachment) langsung dipindahkan begitu material dipilih (row dengan file_code
					// dikirim sebagai existing_attachments[] saat Save). Sengaja TIDAK di watcher plant: ganti plant
					// tidak boleh menimpa baris attachment yang sudah ditambah user.
					this.applyLookupDefaults([BASIC_DATA_TAB, MRP_TAB, MANUFACTURE_TAB, ACCOUNTING_TAB, ATTACHMENT_TAB]);
					// Material Type baru diketahui dari lookup (MCR002/MCR003): plant hasil lookup / ext_plant yang sudah dipilih
					// yang tidak diizinkan `option_values` (mis. INALUM ZFGD tanpa A002/A0N2) dibuang. Dipanggil langsung, bukan
					// cuma lewat watcher material_type -- material baru dengan Material Type SAMA tidak memicu watcher itu.
					this.pruneDisallowedFieldValues([BASIC_DATA_TAB, EXTEND_DATA_TAB]);
					this.normalizeNumberFields();
					// Label option select kolom (mis. manufacture) di-resolve begitu kode keisi dari lookup --
					// opsi yang belum ke-load di-fetch mengikuti value baris (merge, bukan replace).
					this.preloadRepeatableFieldLabels([MANUFACTURE_TAB]);
					// Nilai hasil lookup (MCR002: data SAP material, termasuk Sparepart Category / MRP Type / Lot Size) dipertahankan:
					// snapshot sumber otomasi diambil SETELAH lookup, jadi formasi ConfigDataMapping (auto_select_single + autofill)
					// baru jalan kalau user mengganti Material Type / Plant sesudahnya (onAutomationSourceChange dari @change).
					this.captureAutomationSnapshot();
				} catch (error) {
					this.materialLookupDetail = null;
					// Biarkan field lookup kosong kalau gagal ambil detail — jangan blok user isi manual.
				}
			},
			// Trigger fetch detail HANYA saat user benar-benar memilih suggestion, bukan tiap ketik
			// (searchMaterialNumber/@complete cuma buat cari opsi, bukan sumber untuk lookup default_value).
			onMaterialNumberSelect(event) {
				const option = event?.value;
				const raw = option?.raw ?? option?.value ?? option;
				this.dialog.form.material_number = option?.value ?? raw;
				this.dialog.form.material_number_raw = raw;
				this.dialog.isMaterialSelected = true;
				// Ganti material: field yang `depends_on` material_number (plant MCR002 -- opsinya ikut material
				// di eu_material_plants) dikosongkan & opsinya dimuat ulang, lalu diisi lagi oleh default_value
				// lookup dari detail material yang baru (lihat fetchMaterialLookupDetail).
				this.onFieldDependencyChange('material_number', BASIC_DATA_TAB);
				this.fetchMaterialLookupDetail(raw);
			},
			async openForAdd() {
				this.dialog.editingItemId = null;
				this.resetForm();
				this.applyStaticDefaultValue([BASIC_DATA_TAB, MRP_TAB, ACCOUNTING_TAB]);
				this.dialog.internalVisible = true;
				// Baseline snapshot DIAMBIL SETELAH default value ke-apply -- supaya watcher dialog.form
				// tidak salah anggap default awal itu "transisi", cuma capture state apa adanya.
				this.captureEnabledSnapshot();

				// await this.loadDialogOptionsS();
			},
			// Dipanggil parent (UserForm.vue) lewat ref dengan `mcr_item_code` (bukan id) -- viewData.emcr_item
			// sekarang berasal dari endpoint "simple" (request-list/simple/{id}) yang SENGAJA tidak eager-load
			// emcrItemManufacturer/emcrItemPlant/emcrItemAttachment/emcrItemValuation, jadi detail lengkapnya
			// tetap harus di-fetch terpisah lewat request-list/emcr-item. `dialog.editingItemId` diambil dari
			// response fetch ini (item.id), bukan dari parent -- satu sumber kebenaran.
			async openForEdit(mcrItemCode) {
				try {
					const response = await api(this, requestListService.detailEmcrItem({ mcr_item_code: mcrItemCode }), false);
					const item = response?.data;
					if (!item) {
						this.$toastNotifPrime('error', 'Error', 'Item not found.');
						return;
					}

					this.resetForm();
					this.dialog.editingItemId = item.id;
					this.hydrateFormFromItem(item);
					this.dialog.internalVisible = true;
					// Baseline snapshot DIAMBIL SETELAH hydrate -- kalau diambil sebelum (mis. di
					// resetForm()), snapshot bakal berbasis form kosong, bukan value hasil hydrate.
					this.captureEnabledSnapshot();
					// SENGAJA TIDAK di-await -- dialog sudah kebuka (internalVisible = true) duluan,
					// label field select yang cuma nampilin raw code nge-resolve jadi "code - description"
					// begitu fetch-nya selesai di background (lihat DECISIONS_LOG.md).
					this.preloadSelectedFieldLabels([BASIC_DATA_TAB, MRP_TAB, EXTEND_DATA_TAB, ACCOUNTING_TAB]);
					this.preloadRepeatableFieldLabels([MANUFACTURE_TAB]);
				} catch (error) {
					console.error('Error fetching item detail:', error);
				}
			},
			// Kebalikan dari buildSaveFormData() -- isi dialog.form dari record emcr_item + relasinya
			// (hasil request-list/emcr-item). Nama relasi di response ikut aturan snake_case Eloquent
			// (emcrItemManufacturer -> emcr_item_manufacturer, dst). SENGAJA TIDAK manggil
			// fetchMaterialLookupDetail: applyLookupDefaults() nge-overwrite value TANPA cek "sudah keisi
			// atau belum" (beda dari applyStaticDefaultValue), jadi kalau dipanggil di sini value yang
			// sudah tersimpan bisa ketiban default lookup yang salah.
			hydrateFormFromItem(item) {
				const form = this.dialog.form;

				form.mcr_code = item.mcr_code || form.mcr_code;
				form.material_number = materialNumberFormatter.formatMaterialNumber(item.material_number || '');
				form.material_number_raw = item.material_number || '';
				// Dianggap "sudah dipilih" dari awal (bukan hasil ngetik) supaya enabled/depends_on yang
				// gantung ke material_number langsung aktif di mode edit -- lihat catatan isMaterialSelected
				// di DECISIONS_LOG.md.
				this.dialog.isMaterialSelected = !!item.material_number;
				form.material_type = item.material_type || '';
				form.material_group = item.material_group || '';
				form.item_type = item.item_type || '';
				// Plant multi-select: daftar plant tersimpan diambil dari baris emcr_item_plants (satu baris
				// per plant yang dipilih), fallback ke kolom tunggal emcr_items.plant (item lama / material
				// type selain ZFGD yang cuma punya satu baris).
				const savedPlants = [...new Set((item.emcr_item_plant || []).map((plant) => plant.plant).filter(Boolean))];
				form.plant = savedPlants.length ? savedPlants : (item.plant ? [item.plant] : []);
				form.equipment = item.equipment || '';
				form.uom = item.uom || '';
				form.currency = item.currency || '';
				form.price_reference = item.price_reference || '';
				form.price_unit = item.price_unit ?? null;
				form.service_group = item.service_group || '';
				form.service_category = item.service_category || '';
				form.specification = item.specification || '';
				form.notes = item.notes || '';
				form.valuation_class = item.valuation_class || '';
				form.standard_description = item.standard_description || '';
				form.estimated_annual_usage = item.estimated_annual_usage ?? null;
				form.repairable_item = item.repairable_item ?? null;

				const manufacturers = item.emcr_item_manufacturer || [];
				form.emcr_manufacturers = manufacturers.length
					? manufacturers.map((m) => ({
						old_manufacture: '',
						old_manufacture_ref: '',
						manufacture: m.manufacture || '',
						manufacture_ref: m.manufacture_ref || '',
						notes: m.notes || '',
					}))
					: [{ old_manufacture: '', old_manufacture_ref: '', manufacture: '', manufacture_ref: '', notes: '' }];

				// MRP -- 1 baris per emcr_item (lihat UserFormService::save, emcrItemPlant->create tunggal).
				const mrp = (item.emcr_item_plant || [])[0];
				form.sparepart_category = mrp?.sparepart_category || '';
				form.mrp_type = mrp?.mrp_type || '';
				form.lot_size = mrp?.lot_size || '';
				form.reorder_point = mrp?.reorder_point ?? null;
				form.safety_stock = mrp?.safety_stock ?? null;
				form.abc_indicator = mrp?.abc_indicator || '';
				form.maximum_stock_level = mrp?.maximum_stock_level ?? null;
				form.rounding_value = mrp?.rounding_value ?? null;

				// Extend Data
				[
					'ext_plant', 'ext_location', 'ext_valuation_type', 'ext_sales_org',
					'ext_dist_channel', 'ext_warehouse_number', 'ext_storage_type',
					'ext_cf_plant', 'ext_cf_location', 'ext_cf_valuation_type', 'ext_cf_sales_org',
					'ext_cf_dist_channel', 'ext_cf_warehouse_number', 'ext_cf_storage_type',
				].forEach((key) => { form[key] = item[key] || ''; });

				// Accounting
				const valuation = (item.emcr_item_valuation || [])[0];
				form.price_control = valuation?.price_control || '';
				form.std_price = valuation?.std_price ?? null;
				// Valuation Class tab Accounting (ANTAM MCR003) disimpan di emcr_item_valuations (path emcr_valuations.0), bukan
				// emcr_items.valuation_class -- path Basic Data itu di-null-kan Guard BE karena field Basic Data-nya tidak tampil di MCR003.
				if (!form.valuation_class) form.valuation_class = valuation?.valuation_class || '';

				// Attachments
				form.emcr_attachments = (item.emcr_item_attachment || []).map((a) => ({
					name: a.file_attachment?.original_filename || a.file_attachment?.file_name || a.file_code || '',
					size: a.file_attachment?.file_size ? Number(a.file_attachment.file_size) : 0,
					file_code: a.file_code || '',
					file_attachment_id: a.file_attachment?.id || null,
					mime_type: a.file_attachment?.mime_type || '',
				}));

				this.normalizeNumberFields();
			},
			// Kolom decimal dari API datang sebagai string ("0.00"); InputNumber butuh number, kalau tidak field tidak bisa
			// diketik (spin menyambung teks, perbandingan nilai lama/baru beda tipe). String angka -> number, "" -> null.
			// Dipanggil setelah nilai field diisi dari API: lookup Material Number, lookup per plant / plant Copy From, dan
			// hydrate mode Edit.
			normalizeNumberFields() {
				NUMBER_INPUT_FIELDS.forEach((field) => {
					const raw = this.dialog.form[field];
					if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) this.dialog.form[field] = Number(raw);
					else if (raw === '') this.dialog.form[field] = null;
				});
			},
			resetForm() {
				this.dialog.validated = false;
				this.dialog.errors = {};
				this.dialog.isMaterialSelected = false;
				this.dialog.form = this.emptyRow();
				this.dialog.sections = { basicData: true, manufacturerPartNumber: true, mrp: true, attachment: true, orgalizationLevel: true, accounting: true }
				// this.clearSuggestions();
			},
			onHide() {
				this.dialog.internalVisible = false;
				this.$emit('close');

				console.log(this.dialog.internalVisible)
			},
			// === Save ===
			// Bangun FormData langsung dari dialog.form (nama field kanonik) ke nama field yang dibaca
			// backend (UserFormRequest.php/UserFormService.php) -- lihat komentar per section di bawah.
			// userFormService.create()/update() sekarang cuma bertugas POST FormData ini apa adanya
			// (bukan convert internal lagi, lihat catatan di userFormService.js).
			// Plant utama = plant pertama yang dipilih user. Dipakai mesin config (ctx) & field yang cuma
			// bisa menampung satu plant (mis. valuation_area di tab Accounting).
			primaryPlant() {
				const plants = this.dialog.form.plant;
				return Array.isArray(plants) ? (plants[0] || '') : (plants || '');
			},
			buildSaveFormData() {
				const form = this.dialog.form;
				const fd = new FormData();
				const put = (key, value) => {
					if (value !== undefined && value !== null) fd.append(key, value);
				};

				put('mcr_code', form.mcr_code || '');
				put('mcr_type', (this.mcrType || '').toUpperCase());
				// material_number yang dikirim ke backend harus versi RAW (belum di-strip leading zero
				// oleh materialNumberFormatter) -- lihat catatan material_number_raw di DECISIONS_LOG.md.
				put('material_number', form.material_number_raw || form.material_number || '');
				put('material_type', form.material_type || '');
				put('material_group', form.material_group || '');
				put('item_type', form.item_type || '');
				// Plant multi-select -> plant[i]; BE menyimpan plant pertama sebagai plant utama item dan
				// menggandakan baris MRP / data cataloguer per plant yang dipilih.
				const plants = Array.isArray(form.plant) ? form.plant : (form.plant ? [form.plant] : []);
				plants.forEach((plant, index) => put(`plant[${index}]`, plant));
				put('equipment', form.equipment || '');
				put('uom', form.uom || '');
				put('currency', form.currency || '');
				put('price_reference', form.price_reference || '');
				put('price_unit', form.price_unit ?? '');
				put('service_group', form.service_group || '');
				put('service_category', form.service_category || '');
				put('specification', form.specification || '');
				put('notes', form.notes || '');
				put('valuation_class', form.valuation_class || '');
				put('standard_description', form.standard_description || '');
				put('estimated_annual_usage', form.estimated_annual_usage ?? '');
				// repairable_item: kirim 1 (true) / 0 (false), skip kalau belum diisi sama sekali.
				if (form.repairable_item !== undefined && form.repairable_item !== null && form.repairable_item !== '') {
					put('repairable_item', form.repairable_item === true || form.repairable_item === '1' || form.repairable_item === 1 ? 1 : 0);
				}

				if (this.isTabVisibleFn(MANUFACTURE_TAB)) {
					(form.emcr_manufacturers || []).forEach((m, index) => {
						if (!m.manufacture) return; // skip baris placeholder kosong
						put(`emcr_manufacturers[${index}][manufacture]`, m.manufacture);
						put(`emcr_manufacturers[${index}][manufacture_ref]`, m.manufacture_ref || '');
						put(`emcr_manufacturers[${index}][manufacture_ref_description]`, m.notes || '');
					});
				}

				if (this.isTabVisibleFn(MRP_TAB)) {
					const mrpFields = {
						sparepart_category: form.sparepart_category,
						mrp_type: form.mrp_type,
						lot_size: form.lot_size,
						reorder_point: form.reorder_point,
						safety_stock: form.safety_stock,
						abc_indicator: form.abc_indicator,
						maximum_stock_level: form.maximum_stock_level,
						rounding_value: form.rounding_value,
					};
					Object.entries(mrpFields).forEach(([key, value]) => put(`emcr_mrp[${key}]`, value ?? ''));
				}

				if (this.isTabVisibleFn(EXTEND_DATA_TAB)) {
					[
						'ext_plant', 'ext_location', 'ext_valuation_type', 'ext_sales_org',
						'ext_dist_channel', 'ext_warehouse_number', 'ext_storage_type',
						'ext_cf_plant', 'ext_cf_location', 'ext_cf_valuation_type', 'ext_cf_sales_org',
						'ext_cf_dist_channel', 'ext_cf_warehouse_number', 'ext_cf_storage_type',
					].forEach((key) => put(key, form[key] || ''));
				}

				// emcr_valuations -- cuma dikirim kalau user benar-benar isi salah satu field Accounting.
				if (this.isTabVisibleFn(ACCOUNTING_TAB) && (form.price_control || form.std_price != null)) {
					put('emcr_valuations[0][valuation_area]', form.ext_plant || this.primaryPlant());
					put('emcr_valuations[0][valuation_class]', form.valuation_class || '');
					put('emcr_valuations[0][price_control]', form.price_control || '');
					put('emcr_valuations[0][std_price]', form.std_price ?? '');
				}

				// Attachment: File/Blob asli (baru dipilih, belum diupload) -> emcr_attachments[];
				// record lama ber-file_code (mode edit) -> existing_attachments[].
				(form.emcr_attachments || []).forEach((att) => {
					if (!att) return;
					const isFileLike = (typeof File !== 'undefined' && att instanceof File)
						|| (typeof Blob !== 'undefined' && att instanceof Blob);
					if (isFileLike) {
						fd.append('emcr_attachments[]', att, att.name || 'attachment');
					} else if (att.file_code) {
						fd.append('existing_attachments[]', att.file_code);
					}
				});

				return fd;
			},
			// Baseline "field mana saja yang lagi enabled sekarang" -- dipanggil sekali sesudah form
			// selesai di-set (openForAdd/openForEdit), BUKAN tiap perubahan. Field repeatable (columns/
			// row_rules) di luar scope -- konsep enabled-nya per-baris/per-kolom, beda mekanisme.
			captureEnabledSnapshot() {
				this.captureAutomationSnapshot();
				const snapshot = {};
				Object.keys(TAB_SECTION_META).forEach((tab) => {
					const fields = formConfig.getTabFieldMap(this.formConfig, tab) || {};
					Object.entries(fields).forEach(([field, fc]) => {
						if (fc.columns || fc.row_rules) return;
						snapshot[field] = formConfig.isFieldEnabled(this.formConfig, tab, field, this.formConfigCtx);
					});
				});
				this.enabledFieldSnapshot = snapshot;
			},
			// Field sumber `depends_on` milik field ber-`auto_select_single` (config, tab mana pun) -- mis. Material Type & Plant untuk
			// Sparepart Category INALUM. Nilainya disimpan saat form selesai di-set (add / hydrate edit), supaya cuma perubahan
			// SESUDAHNYA (dipilih user / hasil lookup MCR002) yang memicu refreshAutoSelectDependents -- nilai tersimpan tidak direset.
			automationSourceFields() {
				const names = new Set();
				Object.keys(this.formConfig?.tab_fields || {}).forEach((tab) => {
					Object.entries(formConfig.getTabFieldMap(this.formConfig, tab) || {}).forEach(([field, fc]) => {
						if (!fc?.auto_select_single) return;
						formConfig.getFieldDependsOn(this.formConfig, tab, field, this.formConfigCtx).forEach((name) => names.add(name));
					});
				});
				return [...names];
			},
			captureAutomationSnapshot() {
				this.automationSnapshot = Object.fromEntries(
					this.automationSourceFields().map((name) => [name, this.formConfigCtx.getField(name) ?? ''])
				);
			},
			// Dipanggil @change Material Type / Plant & setelah lookup MCR002: field sumber yang nilainya beda dari snapshot dianggap
			// berubah (Plant multi: cuma plant PERTAMA yang dibaca ctx, jadi menambah plant kedua tidak memicu apa-apa).
			onAutomationSourceChange() {
				const changed = this.automationSourceFields().filter((name) =>
					String(this.formConfigCtx.getField(name) ?? '') !== String(this.automationSnapshot?.[name] ?? ''));
				if (!changed.length) return;
				changed.forEach((name) => { this.automationSnapshot[name] = this.formConfigCtx.getField(name) ?? ''; });
				this.refreshAutoSelectDependents(changed);
			},
			// @change field yang punya `autofill` (mis. Sparepart Category -> MRP Type & Lot Size): reset field yang depends_on field
			// ini lalu isi dari config `autofill`.
			onFieldChangeWithAutofill(field, tab) {
				this.onFieldDependencyChange(field, tab);
				this.applyFieldAutofill(field, tab);
			},
			// Field yang baru saja TRANSISI dari enabled -> disabled (dibandingkan ke
			// enabledFieldSnapshot, bukan cuma current state) di-reset: pakai default_value yang match
			// context sekarang kalau ada (type "static" langsung di-apply ulang; type lain, mis. "lookup",
			// dibiarkan -- itu urusan applyLookupDefaults/watcher trigger-nya sendiri), atau balik ke value
			// kosong bawaan emptyRow() kalau field itu memang tidak punya default_value sama sekali.
			// Dipanggil dari watcher deep `dialog.form`.
			// Field yang sama (state dialog.form dipakai bersama) masih TAMPIL & enabled di tab lain -> jangan dikosongkan karena tab ini
			// disabled. Mis. valuation_class Basic Data (MCR003: disabled) & Accounting (MCR003 ANTAM: enabled): snapshot enabled di-key per
			// nama field, jadi status Accounting terbaca sebagai transisi enabled -> disabled di Basic Data -> Valuation Class FORM01 MCR003
			// ANTAM dulu terhapus tiap form berubah (Save Item gagal "required"). Snapshot SENGAJA tetap per nama field (perilaku INALUM/TIMAH
			// tidak berubah); field yang cuma ada di satu tab selalu false.
			isFieldEditableInOtherTab(field, currentTab) {
				return Object.keys(TAB_SECTION_META).some((tab) => tab !== currentTab
					&& formConfig.getTabFieldMap(this.formConfig, tab)?.[field]
					&& formConfig.isFieldVisible(this.formConfig, tab, field, this.formConfigCtx)
					&& formConfig.isFieldEnabled(this.formConfig, tab, field, this.formConfigCtx));
			},
			clearDisabledFieldsWithoutDefault() {
				const emptyValues = this.emptyRow();
				const nextSnapshot = {};

				Object.keys(TAB_SECTION_META).forEach((tab) => {
					const fields = formConfig.getTabFieldMap(this.formConfig, tab) || {};
					Object.entries(fields).forEach(([field, fc]) => {
						if (fc.columns || fc.row_rules) return;

						const isEnabled = formConfig.isFieldEnabled(this.formConfig, tab, field, this.formConfigCtx);
						const wasEnabled = this.enabledFieldSnapshot[field];
						nextSnapshot[field] = isEnabled;

						// Field terkunci yang tetap disimpan (`readonlyandsaved`, mis. MRP INALUM MCR001 non-ZFGD/ZSFG & MCR002 ZNNV/ZNOV):
						// nilainya SELALU default static yang cocok saat ini (tanpa default -> kosong), sama dengan FORM02
						// applyLockedDefaults -- termasuk saat field memang disabled dari awal (MCR002: Material Type baru diketahui
						// setelah lookup Material Number, jadi tidak ada transisi enabled -> disabled). Mode lihat-saja dilewati.
						if (!this.readonly && fc.readonlyandsaved && isEnabled === false
							&& formConfig.isFieldVisible(this.formConfig, tab, field, this.formConfigCtx)
							&& formConfig.isReadonlySaved(this.formConfig, tab, field, this.formConfigCtx)) {
							const entry = formConfig.resolveActiveDefault(this.formConfig, tab, field, this.formConfigCtx);
							const next = entry?.type === 'static' ? entry.value : (entry ? this.dialog.form[field] : emptyValues[field]);
							if (JSON.stringify(this.dialog.form[field] ?? null) !== JSON.stringify(next ?? null)) this.dialog.form[field] = next;
							return;
						}

						if (wasEnabled === true && isEnabled === false && !this.isFieldEditableInOtherTab(field, tab)) {
							const entry = formConfig.resolveActiveDefault(this.formConfig, tab, field, this.formConfigCtx);
							if (!entry) {
								this.dialog.form[field] = emptyValues[field];
							} else if (entry.type === 'static') {
								this.dialog.form[field] = entry.value;
							}
							// type lain (mis. "lookup") sengaja dibiarkan -- bukan tanggung jawab watcher ini.
						}
					});
				});

				this.enabledFieldSnapshot = nextSnapshot;
			},
			// Validasi FE murni reuse formConfig.js -- field mana yang wajib 100% berasal dari `rules[]`/
			// `row_rules` di JSON config (fungsi sama yang sudah dipakai isFieldMandatoryFn/dst buat tanda
			// `*`), BUKAN aturan baru yang di-hardcode di sini. Return true kalau lolos, false kalau ada
			// field kosong -- errornya ditaro di dialog.errors (field-level + section-level) supaya
			// binding `:invalid`/`border-danger`/small error text yang sudah ada di template langsung aktif.
			validateForm() {
				this.dialog.errors = {};
				let hasError = false;

				const markSectionError = (tab) => {
					hasError = true;
					const meta = TAB_SECTION_META[tab];
					if (!meta) return;
					this.dialog.errors[meta.errorKey] = 'Please complete the required fields.';
					this.dialog.sections[meta.sectionKey] = true; // auto-expand section yang lagi collapsed
				};

				Object.keys(TAB_SECTION_META).forEach((tab) => {
					if (!this.isTabVisibleFn(tab)) return;
					const fields = formConfig.getTabFieldMap(this.formConfig, tab) || {};

					Object.entries(fields).forEach(([field, fc]) => {
						// Field bertingkat ber-kolom (mis. Manufacture -> emcr_manufacturers.columns.*) --
						// error-nya disimpan PER BARIS (row.rowErrors), bukan di dialog.errors flat,
						// karena satu tab bisa punya banyak baris dengan validitas beda-beda. Template
						// bind :invalid ke `data.rowErrors?.<column>` (lihat kolom manufacture/manufacture_ref).
						if (fc.columns) {
							(this.dialog.form[field] || []).forEach((row) => {
								row.rowErrors = {};
								Object.keys(fc.columns).forEach((column) => {
									if (!formConfig.isColumnVisible(this.formConfig, tab, field, column, this.formConfigCtx)) return;
									if (!formConfig.isColumnMandatory(this.formConfig, tab, field, column, this.formConfigCtx)) return;
									if (!formConfig.isFilledValue(row[column])) {
										row.rowErrors[column] = true;
										markSectionError(tab);
									}
								});
							});
							return;
						}

						// Field bertingkat tanpa kolom (mis. Attachment) -- row_rules min_rows/required
						if (fc.row_rules) {
							const rows = this.dialog.form[field] || [];
							const minRowsRule = fc.row_rules.find((r) => r.type === 'min_rows');
							const requiredRule = fc.row_rules.find((r) => r.type === 'required');
							const minRows = minRowsRule ? minRowsRule.value : (requiredRule ? 1 : 0);
							if (minRows > 0 && rows.length < minRows) markSectionError(tab);
							return;
						}

						// Field biasa
						if (!formConfig.isFieldVisible(this.formConfig, tab, field, this.formConfigCtx)) return;
						if (!formConfig.isFieldMandatory(this.formConfig, tab, field, this.formConfigCtx)) return;
						if (!formConfig.isFilledValue(this.dialog.form[field])) {
							this.dialog.errors[field] = 'This field is required.';
							markSectionError(tab);
						}
					});
				});

				return !hasError;
			},
			// Error BE `assertMaterialNumberNotRunning` ("Material Number X On Progress in: + MCR ...") -> $swal error dengan pesan
			// lengkapnya (awalan "Failed to create emcr items:" dibuang). true = sudah ditangani; selain itu toast error seperti
			// biasa (api(..., false) tidak menampilkan toast sendiri) lalu pemetaan error field.
			showMaterialNumberRunningError(error) {
				const raw = error?.response?.data?.message;
				const message = typeof raw === 'string' ? raw.replace(/^Failed to (create|update) emcr items:\s*/i, '') : '';
				if (!/^Material Number .+ On Progress in:/.test(message)) {
					this.$toastNotifPrime('error', 'Error', message || raw || error?.message || 'Something went wrong');
					return false;
				}
				const escaped = message.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
				this.$swal({
					icon: 'error',
					title: 'Error',
					html: `<div style="text-align: left; white-space: pre-line;">${escaped}</div>`,
					confirmButtonText: 'Close',
				});
				return true;
			},
			// Mapping error validasi 422 dari backend ke dialog.errors
			applyServerErrors(error) {
				const data = error?.response?.data?.data;
				if (!data || typeof data !== 'object') return;

				Object.entries(data).forEach(([key, messages]) => {
					const message = Array.isArray(messages) ? messages[0] : messages;

					// emcr_mrp.<field> -- key-nya sekarang sama persis dengan nama field FE
					// (dialog.form.sparepart_category dst), tidak perlu translate lagi.
					const mrpMatch = key.match(/^emcr_mrp\.(.+)$/);
					if (mrpMatch) {
						const field = mrpMatch[1];
						this.dialog.errors[field] = message;
						this.dialog.errors.mrp = this.dialog.errors.mrp || 'Please check the highlighted fields.';
						this.dialog.sections.mrp = true;
						return;
					}

					if (!key.includes('.')) {
						this.dialog.errors[key] = message;
					}
				});
			},
			async saveItem() {
				if (!this.validateForm()) {
					this.$toastNotifPrime('error', 'Validation Error', 'Please complete the required fields.');
					return;
				}

				this.dialog.saving = true;
				try {
					const formData = this.buildSaveFormData();
					// showMessage false: toast error otomatis `api` ditangani di catch (error Material Number -> $swal).
					const response = this.dialog.editingItemId
						? await api(this, userFormService.update(this.dialog.editingItemId, formData), false)
						: await api(this, userFormService.create(formData), false);
					this.$toastNotifPrime('success', 'Success', response?.message || 'Operation completed successfully');
					this.dialog.internalVisible = false;
					this.$emit('saved');
				} catch (error) {
					console.error('Error saving MCR item:', error);
					if (!this.showMaterialNumberRunningError(error)) {
						this.applyServerErrors(error);
					}
				} finally {
					this.dialog.saving = false;
				}
			},
			openAttachmentPicker() {
				this.$refs.attachmentInput && this.$refs.attachmentInput.click();
			},
			getFileExtension(name) {
				return name ? name.split('.').pop().toLowerCase() : '';
			},
			onAttachmentSelected(event) {
				const files = Array.from(event.target.files || []);
				const validFiles = [];

				// Ekstensi yang diizinkan dibaca dari row_rules `mimes` di form config (FORM01_*.json),
				// SATU sumber kebenaran yang sama dengan validasi BE (FormConfigRuleBuilder::buildTableRules).
				const allowedExt = formConfig.getRowRuleValue(this.formConfig, ATTACHMENT_TAB, 'emcr_attachments', 'mimes') || [];

				files.forEach((file) => {
					const ext = this.getFileExtension(file.name);
					if (!allowedExt.includes(ext)) {
						this.$toastNotifPrime('error', 'Invalid File Type', `${file.name}: only ${allowedExt.join(', ').toUpperCase()} are allowed`);
						return;
					}
					if (file.size > ATTACHMENT_MAX_SIZE) {
						this.$toastNotifPrime('error', 'File Too Large', `${file.name}: exceeds 10MB`);
						return;
					}
					validFiles.push(file);
				});

				if (validFiles.length > 0) {
					this.dialog.form.emcr_attachments.push(...validFiles);
				}
				event.target.value = '';
			},
			formatAttachmentSize(file) {
				const size = file instanceof File ? file.size : file?.size;
				return size ? Math.round(size / 1024) + ' KB' : '';
			},
			isDownloadableAttachment(file) {
				return (file instanceof File) || !!file?.file_attachment_id;
			},
			async downloadAttachment(file) {
				if (file instanceof File) {
					const url = URL.createObjectURL(file);
					const a = document.createElement('a');
					a.href = url;
					a.download = file.name;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					setTimeout(() => URL.revokeObjectURL(url), 100);
					return;
				}
				if (!file?.file_attachment_id) return;
				try {
					const response = await api(this, smartcatAttachmentService.file(file.file_attachment_id), false);
					const blob = response.data instanceof Blob ? response.data : (response instanceof Blob ? response : new Blob([response?.data || '']));
					const url = URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = file.name || 'attachment';
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					setTimeout(() => URL.revokeObjectURL(url), 100);
				} catch (error) {
					this.$toastNotifPrime('error', 'Error', 'Failed to download file');
				}
			},
			isImageFile(file) {
				const ext = this.getFileExtension(file?.name);
				const mime = (file instanceof File ? file.type : file?.mime_type || '').toLowerCase();
				return mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
			},
			isPdfFile(file) {
				const ext = this.getFileExtension(file?.name);
				const mime = (file instanceof File ? file.type : file?.mime_type || '').toLowerCase();
				return mime === 'application/pdf' || ext === 'pdf';
			},
			isPreviewableAttachment(file) {
				if (file instanceof File) {
					return file.type.startsWith('image/') || file.type === 'application/pdf';
				}
				return !!file?.file_attachment_id && (this.isImageFile(file) || this.isPdfFile(file));
			},
			async previewAttachment(file) {
				// File baru (belum diupload) di-preview langsung dari blob lokal;
				if (file instanceof File) {
					const url = URL.createObjectURL(file);
					if (file.type === 'application/pdf') {
						this.previewModal.pdfUrl = url;
						this.previewModal.title = file.name;
						this.previewModal.visible = true;
					} else if (file.type.startsWith('image/')) {
						this.openImagePreview(url);
					}
					return;
				}
				if (!file?.file_attachment_id) return;
				try {
					const response = await api(this, smartcatAttachmentService.file(file.file_attachment_id), false);
					const raw = response.data instanceof Blob ? response.data : (response instanceof Blob ? response : new Blob([response?.data || '']));
					if (this.isPdfFile(file)) {
						const blob = new Blob([raw], { type: 'application/pdf' });
						this.previewModal.pdfUrl = URL.createObjectURL(blob);
						this.previewModal.title = file.name || 'Preview';
						this.previewModal.visible = true;
					} else if (this.isImageFile(file)) {
						this.openImagePreview(URL.createObjectURL(raw));
					}
				} catch (error) {
					this.$toastNotifPrime('error', 'Error', 'Failed to load preview');
				}
			},
			openImagePreview(url) {
				if (this.previewModal.imageUrl && this.previewModal.imageUrl.startsWith('blob:')) {
					URL.revokeObjectURL(this.previewModal.imageUrl);
				}
				this.previewModal.imageUrl = url;
			},
			onPreviewImageLoad() {
				if (this.previewModal.imageUrl && this.previewModal.imageUrl.startsWith('blob:')) {
					this.$nextTick(() => {
						if (this.$refs.imagePreviewer && typeof this.$refs.imagePreviewer.onImageClick === 'function') {
							this.$refs.imagePreviewer.onImageClick();
						}
					});
				}
			},
			removeAttachmentRow(index) {
				this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
					if (result.isConfirmed) {
						this.dialog.form.emcr_attachments.splice(index, 1);
					}
				});
			},
		}
	}
</script>

<style scoped>
.pdf-iframe {
	width: 100%;
	height: 80vh;
	border: none;
	border-radius: 0 0 12px 12px;
}
</style>